import {Placement, Rect} from '@popperjs/core';
import {Modifier} from '@popperjs/core/lib/types';
import clsx from 'clsx';
import {Property} from 'csstype';
import React, {useRef} from 'react';
import {Box, Fade, Popper} from '@mui/material';
import useResizeObserver from '@/hooks/use-resize-observer';
import styles from './FloatingPopperBar.module.scss';
import defaultWidthCalculation from './widthCalculation';

export type FloatingPopperBarProps = {
  testID?: string;
  children: React.ReactNode;
  className?: string;
  isOpen?: boolean;
  placement: Placement;
  /**
   * Compute the popper width from the observed anchor width. Defaults to
   * `defaultWidthCalculation`, which extends the bar 24px past the anchor
   * on each side so its backdrop reaches the canvas edges. Override when
   * the anchor's layout makes that extension overlap something (e.g. a
   * sidebar), or when a different sizing rule is needed.
   */
  widthCalculation?: (width?: number) => Property.Width<number>;
  /**
   * Pixel gap between the bar's outer edge and the **viewport** edge along
   * the `placement` axis (e.g. for `placement="bottom"`, the gap between the
   * bar's bottom and the bottom of the viewport). Defaults to 0 because the
   * visual breathing room around the bar pill is baked into the backdrop
   * wrapper's padding.
   */
  placementMargin?: number;
  /** Rendered above the main bar when provided; parent controls when to render. */
  topContent?: React.ReactNode;
  renderContent?: () => React.ReactNode;
};

function getPopperModifiers(
  additionalMargin = 0
): Array<Partial<Modifier<string, object>>> {
  return [
    {
      name: 'flip',
      enabled: false,
    },
    {
      name: 'preventOverflow',
      enabled: false,
    },
    {
      name: 'offset',
      options: {
        // Anchor the popper to the viewport edge (not the anchor's edge)
        // so the bar stays flush with the page regardless of how tall the
        // anchor element is.
        offset: ({
          placement,
          popper,
          reference,
        }: {
          reference: Rect;
          placement: Placement;
          popper: Rect;
        }) => {
          if (placement === 'bottom') {
            const yShift =
              window.innerHeight -
              popper.height -
              reference.y -
              reference.height -
              additionalMargin;
            return [0, yShift];
          }
          if (placement === 'top') {
            const yShift = -reference.y + additionalMargin;
            return [0, yShift];
          }
          return [0, 0];
        },
      },
    },
  ];
}

function FloatingPopperBar(props: FloatingPopperBarProps) {
  const {
    testID = 'floating-popper-bar',
    children,
    className,
    isOpen = false,
    placement = 'bottom',
    widthCalculation = defaultWidthCalculation,
    topContent,
    renderContent,
    placementMargin = 0,
  } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const {width} = useResizeObserver(containerRef);
  return (
    <>
      <div
        className={clsx(styles.container, className)}
        ref={containerRef}
        data-testid={testID}
      >
        {children}
      </div>
      {containerRef.current && (
        <Popper
          id="floating-popper-bar"
          anchorEl={containerRef.current || undefined}
          open={isOpen}
          placement={placement}
          popperOptions={{strategy: 'fixed'}}
          transition
          modifiers={getPopperModifiers(placementMargin)}
          style={{width: widthCalculation(width)}}
        >
          {({TransitionProps}) => (
            <Fade {...TransitionProps} timeout={350}>
              <div className={styles.popperContent}>
                {topContent}
                <Box className={styles.floatingPopperBar}>
                  {renderContent?.()}
                </Box>
              </div>
            </Fade>
          )}
        </Popper>
      )}
    </>
  );
}

export default FloatingPopperBar;
